<script setup lang="ts">
   import { computed } from 'vue';
   import type { RouteLocationRaw } from 'vue-router';

   export type FeatureCardProps = {
      icon: string;
      title: string;
      description: string;
      buttonText?: string;
      buttonDisabled?: boolean;
      delay?: number;
      to?: RouteLocationRaw;
   };

   const props = withDefaults(defineProps<FeatureCardProps>(), {
      buttonDisabled: true,
      delay: 100,
   });

   const aosDelay = computed(() => props.delay.toString());
</script>

<template>
   <div
      class="rounded-lg border-2 border-brand-primary bg-white p-6"
      data-aos="fade-up"
      :data-aos-delay="aosDelay"
      data-aos-duration="600"
      data-aos-easing="ease-in-out"
      data-aos-once="true"
      data-aos-offset="20"
      data-aos-anchor-placement="bottom-bottom">
      <div class="mb-4 flex justify-center">
         <slot name="icon">
            <div class="flex size-16 items-center justify-center rounded-full bg-brand-title">
               <i :class="`pi ${icon} text-white`" style="font-size: 2rem !important;"></i>
            </div>
         </slot>
      </div>
      <h3 class="mb-3 text-center text-xl font-bold text-brand-secondary">
         {{ title }}
      </h3>
      <p class="text-center text-gray-700">
         {{ description }}
      </p>
      <div v-if="$slots.action || buttonText" class="mt-6 flex justify-center">
         <slot name="action">
            <!-- Use NuxtLink when 'to' prop is provided -->
            <NuxtLink
               v-if="to && !buttonDisabled"
               :to="to"
               class="flex cursor-pointer items-center justify-center gap-2 rounded-sm border-2 border-brand-secondary bg-brand-secondary px-6 py-3 text-base font-semibold uppercase text-white transition duration-300 hover:border-brand-secondary hover:bg-brand-title">
               <span>{{ buttonText }}</span>
               <i class="pi pi-arrow-right" style="font-size: 1rem !important;"></i>
            </NuxtLink>
            <!-- Fallback to button when disabled or no 'to' prop -->
            <button
               v-else
               :disabled="buttonDisabled"
               class="flex items-center justify-center gap-2 rounded-sm border-2 border-brand-primary bg-brand-button px-6 py-3 text-base font-semibold uppercase text-white transition duration-300"
               :class="buttonDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-brand-title hover:border-brand-secondary cursor-pointer'">
               <span>{{ buttonText }}</span>
               <i class="pi pi-arrow-right" style="font-size: 1rem !important;"></i>
            </button>
         </slot>
      </div>
   </div>
</template>

<style scoped></style>
